import { useState, useCallback, useEffect, useRef } from 'react';
import ExportService from '../services/ExportService';

export const useJournalManager = (journalStorageService, azureOpenAIService, conversationManager) => {
  // Journal content states
  const [conversationSummary, setConversationSummary] = useState('');
  const [emotionalInsights, setEmotionalInsights] = useState('');
  const [summaryHeader, setSummaryHeader] = useState('');
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isAutoSaved, setIsAutoSaved] = useState(false);
  const [isLoadingJournal, setIsLoadingJournal] = useState(false);

  // UI states
  const [showSummary, setShowSummary] = useState(false);
  const [showViewJournalButton, setShowViewJournalButton] = useState(false);
  const [activeTab, setActiveTab] = useState('Listen');
  
  // Display states for typing animations
  const [displayedHeader, setDisplayedHeader] = useState('');
  const [displayedSummary, setDisplayedSummary] = useState('');
  const [displayedInsights, setDisplayedInsights] = useState('');
  const [typingTimeouts, setTypingTimeouts] = useState([]);
  
  // Journal request states - these are the key ones for the navigation
  const [journalRequested, setJournalRequested] = useState(false);
  const [takingNoteMessage, setTakingNoteMessage] = useState(false);
  const [journalButtonClicked, setJournalButtonClicked] = useState(false);
  const [processingJournalRequest, setProcessingJournalRequest] = useState(false);
  
  // Use ref to track latest session state for cleanup
  const sessionStateRef = useRef();
  sessionStateRef.current = conversationManager?.sessionActive;

  // Debug state changes
  useEffect(() => {
    console.log('🔥 [Journal Hook] takingNoteMessage changed to:', takingNoteMessage);
  }, [takingNoteMessage]);

  useEffect(() => {
    console.log('🔥 [Journal Hook] journalButtonClicked changed to:', journalButtonClicked);
  }, [journalButtonClicked]);

  useEffect(() => {
    console.log('🔥 [Journal Hook] journalRequested changed to:', journalRequested);
  }, [journalRequested]);

  // =============================================================================
  // UTILITY FUNCTIONS FIRST (defined before they're used)
  // =============================================================================

  // Clear journal data
  const clearJournal = useCallback(() => {
    console.log('🔥 [Journal Hook] Clearing all journal data');
    
    setConversationSummary('');
    setEmotionalInsights('');
    setSummaryHeader('');
    setDisplayedHeader('');
    setDisplayedSummary('');
    setDisplayedInsights('');
    setIsAutoSaved(false);
    setShowSummary(false);
    setShowViewJournalButton(false);
    setJournalRequested(false);
    setTakingNoteMessage(false);
    setJournalButtonClicked(false);
    setProcessingJournalRequest(false);
    
    // Clear typing timeouts
    typingTimeouts.forEach(timeout => clearTimeout(timeout));
    setTypingTimeouts([]);
    
    console.log('🔥 [Journal Hook] Journal data cleared');
  }, [typingTimeouts]);

  // Typing animation functions


  const typeHeaderText = useCallback((headerText, setter, onComplete) => {
    if (!headerText) return;

    let currentIndex = 0;
    
    const typeNextChar = () => {
      if (currentIndex >= headerText.length) {
        setter(headerText);
        setTimeout(() => {
          const headerElements = document.querySelectorAll('.summary-header');
          headerElements.forEach(el => el.classList.add('typing-complete'));
        }, 10);
        if (onComplete) onComplete();
        return;
      }

      const char = headerText[currentIndex];
      const displayText = headerText.substring(0, currentIndex + 1);
      setter(displayText);
      currentIndex++;
      
      let speed = 2;
      if (char === ' ') speed = 5;
      
      const timeout = setTimeout(typeNextChar, speed);
      setTypingTimeouts(prev => [...prev, timeout]);
    };

    typeNextChar();
  }, []);

  // =============================================================================
  // AUTO-SAVE FUNCTION (defined before generateConversationSummary uses it)
  // =============================================================================

  // Auto-save existing journal
const autoSaveExistingJournal = useCallback(async () => {
  if (!journalStorageService || isAutoSaved) return;
  
  try {
    const conversationStats = conversationManager?.getConversationStats?.() || {};
    
    const journalData = {
      header: summaryHeader || 'Today\'s Reflection',
      summary: conversationSummary,
      insights: emotionalInsights,
      conversation: conversationManager?.conversation || [],
      sessionStartTime: conversationManager?.sessionStartTime,
      userId: conversationManager?.userId || 'user_' + Date.now(),
      stats: conversationStats, // Include conversation stats
    };
    
    const savedJournal = journalStorageService.saveJournal(journalData);
    setIsAutoSaved(true);
    
    console.log('🔥 [Journal Hook] Existing journal auto-saved:', savedJournal);
    
  } catch (error) {
    console.error('Auto-save of existing journal failed:', error);
  }
}, [journalStorageService, isAutoSaved, summaryHeader, conversationSummary, emotionalInsights, conversationManager]);

  // =============================================================================
  // CORE JOURNAL FUNCTIONS
  // =============================================================================

  // Generate conversation summary
  const generateConversationSummary = useCallback(async (conversationData, showDropdown = true) => {
    if (!azureOpenAIService || !conversationData || conversationData.length === 0) {
      throw new Error('No conversation to summarize or Azure OpenAI not configured');
    }
    
    console.log('🔥 [Journal Hook] Generating summary, showDropdown:', showDropdown);
    
    if (showDropdown) {
      setShowSummary(true);
    }
    
    setIsGeneratingSummary(true);
    setIsAutoSaved(false);
    setDisplayedHeader('');
    setDisplayedSummary('');
    setDisplayedInsights('');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const summaryResult = await azureOpenAIService.summarizeConversation(conversationData);
      
      if (typeof summaryResult === 'object' && summaryResult.header && summaryResult.content) {
        setSummaryHeader(summaryResult.header);
        setConversationSummary(summaryResult.content);
      } else {
        setSummaryHeader('Today\'s Reflection');
        setConversationSummary(summaryResult);
      }
      
      const hasEmotionalData = conversationData.some(msg => msg.emotions && msg.emotions.length > 0);
      
      if (hasEmotionalData) {
        const insights = await azureOpenAIService.generateEmotionalInsights(conversationData);
        setEmotionalInsights(insights);
      }
      
      // Save to data store if available
      if (conversationManager?.saveConversationToDataStore) {
        await conversationManager.saveConversationToDataStore({
          summary: typeof summaryResult === 'object' ? summaryResult.content : summaryResult,
          summaryHeader: summaryResult.header || 'Today\'s Reflection',
          insights: hasEmotionalData ? await azureOpenAIService.generateEmotionalInsights(conversationData) : ''
        });
      }
      
      // Auto-save if showing dropdown (user requested to view)
      if (showDropdown && journalStorageService) {
        try {
          const journalData = {
            header: summaryResult.header || 'Today\'s Reflection',
            summary: typeof summaryResult === 'object' ? summaryResult.content : summaryResult,
            insights: hasEmotionalData ? await azureOpenAIService.generateEmotionalInsights(conversationData) : '',
            conversation: conversationData,
            sessionStartTime: conversationManager?.sessionStartTime,
            userId: conversationManager?.userId || 'user_' + Date.now(),
          };
          
          const savedJournal = journalStorageService.saveJournal(journalData);
          setIsAutoSaved(true);
          console.log('🔥 [Journal Hook] Journal auto-saved successfully:', savedJournal);
          
        } catch (autoSaveError) {
          console.error('Auto-save failed:', autoSaveError);
        }
      }
      
    } catch (error) {
      console.error('Summary generation error:', error);
      throw error;
    } finally {
      setIsGeneratingSummary(false);
    }
  }, [azureOpenAIService, conversationManager, journalStorageService]);

  // =============================================================================
  // NAVIGATION HANDLERS (defined after all dependencies)
  // =============================================================================

  // Main journal navigation click handler
const handleJournalNavClick = useCallback((isRecording = false) => {
  console.log('🔥 [Journal Hook] handleJournalNavClick called with isRecording:', isRecording);
  console.log('🔥 [Journal Hook] Current state:', {
    sessionActive: conversationManager?.sessionActive,
    isRecording,
    conversationLength: conversationManager?.conversation?.length || 0,
    hasMeaningfulContent: conversationManager?.hasMeaningfulContent?.() || false,
    takingNoteMessage,
    journalButtonClicked,
    journalRequested
  });
  
  // Check if session is active (use isRecording parameter or sessionActive)
  if (isRecording || conversationManager?.sessionActive) {
    console.log('🔥 [Journal Hook] Session is active - setting taking note states');
    
    // Only allow journaling if there's meaningful content or session just started
    if ((conversationManager?.hasMeaningfulContent?.() || conversationManager?.conversation?.length > 0)) {
      // Set all the states for "taking note" mode
      setJournalRequested(true);
      setJournalButtonClicked(true);  
      setTakingNoteMessage(true);
      
      // Don't show dropdown during session
      setShowSummary(false);
      setShowViewJournalButton(false);
      
      console.log('🔥 [Journal Hook] Taking note states set - waiting for session to end');
    } else {
      console.log('🔥 [Journal Hook] No meaningful content yet to journal');
    }
    
  } else {
    console.log('🔥 [Journal Hook] Session not active - handling normally');
    
    // Session not active - handle normally
    if (conversationManager?.hasMeaningfulContent?.()) {
      if (conversationSummary || emotionalInsights) {
        // Show existing summary
        setShowSummary(true);
        console.log('🔥 [Journal Hook] Showing existing summary');
        
        if (!isAutoSaved) {
          autoSaveExistingJournal();
        }
      } else {
        // Generate new summary and show dropdown
        setShowSummary(true);
        console.log('🔥 [Journal Hook] Generating new summary with dropdown');
        generateConversationSummary(conversationManager.conversation, true);
      }
    } else {
      console.log('🔥 [Journal Hook] No meaningful content to journal');
    }
  }
}, [
  conversationManager,
  conversationSummary,
  emotionalInsights,
  isAutoSaved,
  takingNoteMessage,
  journalButtonClicked,
  journalRequested,
  generateConversationSummary,
  autoSaveExistingJournal
]);

// Update the session end effect to be more specific:
useEffect(() => {
  console.log('🔥 [Journal Hook] Session state changed:', {
    sessionActive: conversationManager?.sessionActive,
    journalRequested,
    conversationLength: conversationManager?.conversation?.length || 0,
    hasMeaningfulContent: conversationManager?.hasMeaningfulContent?.() || false
  });

  // If session just ended and journal was requested, show "View Journal" button
  if (!conversationManager?.sessionActive && 
      journalRequested && 
      conversationManager?.hasMeaningfulContent?.()) {
    
    console.log('🔥 [Journal Hook] Session ended with journal request - showing View Journal button');
    
    setProcessingJournalRequest(true);
    
    // Clear the taking note states after a brief delay
    setTimeout(() => {
      console.log('🔥 [Journal Hook] Clearing taking note states');
      setTakingNoteMessage(false);
      setShowSummary(false); // Hide dropdown
    }, 1000);
    
    // Show "View Journal" button after brief delay
    setTimeout(() => {
      setShowViewJournalButton(true);
      setProcessingJournalRequest(false);
      console.log('🔥 [Journal Hook] Showing View Journal button');
    }, 1500);
  }
}, [conversationManager?.sessionActive, journalRequested, conversationManager?.hasMeaningfulContent]);

// In your useJournalManager hook, add this method:
const generateSummaryForEmptyConversation = async () => {
  console.log('🔥 [JournalManager] Generating empty conversation summary');
  setIsGeneratingSummary(true);
  
  try {
    if (azureOpenAIService) {
      const result = await azureOpenAIService.generateEmptyConversationSummary();
      setConversationSummary(result.content);
      setSummaryHeader(result.header);
      setDisplayedHeader(result.header);
      setDisplayedSummary(result.content);
      setIsAutoSaved(true);
    } else {
      // Fallback if no AI service
      setConversationSummary("You stopped by briefly today without diving into anything specific.");
      setSummaryHeader("Quick Check-in");
      setDisplayedHeader("Quick Check-in");
      setDisplayedSummary("You stopped by briefly today without diving into anything specific.");
      setIsAutoSaved(true);
    }
  } catch (error) {
    console.error('Error generating empty summary:', error);
    setConversationSummary("A brief moment of reflection.");
    setSummaryHeader("Quick Visit");
  } finally {
    setIsGeneratingSummary(false);
  }
};
  // Handle view journal button click
  const handleViewJournalClick = useCallback(() => {
    console.log('🔥 [Journal Hook] View Journal button clicked - showing dropdown');
    
    // Hide the "View Journal" button
    setShowViewJournalButton(false);
    
    // Check if we have a summary ready
    if (conversationSummary || emotionalInsights) {
      // Show the dropdown with existing summary
      setShowSummary(true);
      
      // Auto-save if not already saved
      if (!isAutoSaved && journalStorageService) {
        autoSaveExistingJournal();
      }
      
      console.log('🔥 [Journal Hook] Showing existing journal summary in dropdown');
    } else if (conversationManager?.conversation?.length > 0) {
      // Generate summary, show dropdown, and auto-save
      setShowSummary(true);
      
      // Generate the summary (will auto-save since showDropdown=true)
      generateConversationSummary(conversationManager.conversation, true);
      
      console.log('🔥 [Journal Hook] Generating new journal summary and showing in dropdown');
    } else {
      // Not enough conversation content
      console.log('🔥 [Journal Hook] Not enough conversation content');
    }
    
    // Reset the journal request states since we're now showing the journal
    setJournalRequested(false);
    setJournalButtonClicked(false);
    setTakingNoteMessage(false);
  }, [conversationSummary, emotionalInsights, isAutoSaved, journalStorageService, conversationManager, generateConversationSummary, autoSaveExistingJournal]);

  // =============================================================================
  // SESSION END HANDLER
  // =============================================================================

  // Handle session end to process journal requests
  useEffect(() => {
    console.log('🔥 [Journal Hook] Session state changed:', {
      sessionActive: conversationManager?.sessionActive,
      journalRequested,
      conversationLength: conversationManager?.conversation?.length || 0
    });

    // If session just ended and journal was requested, show "View Journal" button
    if (!conversationManager?.sessionActive && journalRequested && (conversationManager?.conversation?.length || 0) > 0) {
      console.log('🔥 [Journal Hook] Session ended with journal request - showing View Journal button');
      
      setProcessingJournalRequest(true);
      
      // Clear the taking note states after a brief delay
      setTimeout(() => {
        console.log('🔥 [Journal Hook] Clearing taking note states');
        setTakingNoteMessage(false);
        setShowSummary(false); // Hide dropdown
      }, 1000);
      
      // Show "View Journal" button after brief delay
      setTimeout(() => {
        setShowViewJournalButton(true);
        setProcessingJournalRequest(false);
        console.log('🔥 [Journal Hook] Showing View Journal button');
      }, 1500);
    }
  }, [conversationManager?.sessionActive, journalRequested, conversationManager?.conversation?.length]);

  // =============================================================================
  // EXPORT FUNCTIONS
  // =============================================================================

  // Download journal
  const downloadJournal = useCallback(() => {
    const markdown = ExportService.generateMarkdown(
      conversationManager?.conversation || [], 
      conversationSummary, 
      emotionalInsights, 
      summaryHeader
    );
    const filename = `voice-journal-${new Date().toISOString().split('T')[0]}.md`;
    ExportService.downloadAsFile(markdown, filename, 'text/markdown');
  }, [conversationManager, conversationSummary, emotionalInsights, summaryHeader]);

  // Export conversation summary
  const exportConversationSummary = useCallback(() => {
    if (!conversationSummary && !emotionalInsights) {
      throw new Error('No summary available to export');
    }
    
    const conversation = conversationManager?.conversation || [];
    const sessionStartTime = conversationManager?.sessionStartTime;
    
    const exportContent = `# ${summaryHeader || 'Voice Journal Session Summary'}\n\n` +
      `**Session Date:** ${new Date().toLocaleDateString()}\n` +
      `**Duration:** ${sessionStartTime ? `${sessionStartTime.toLocaleTimeString()} - ${new Date().toLocaleTimeString()}` : 'N/A'}\n` +
      `**Messages:** ${conversation.length}\n\n` +
      `${conversationSummary}\n\n` +
      `${emotionalInsights ? `---\n\n${emotionalInsights}` : ''}\n\n` +
      `---\n\n## Full Conversation\n\n` +
      conversation
        .filter(msg => msg.type !== 'system')
        .map(msg => {
          const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : '';
          const speaker = msg.type === 'user' ? 'You' : 'EVI';
          return `**${speaker}** ${timestamp}\n${msg.content}`;
        })
        .join('\n\n');
    
    ExportService.downloadAsFile(
      exportContent, 
      ExportService.getFilename(summaryHeader, 'md'), 
      'text/markdown'
    );
  }, [conversationSummary, emotionalInsights, summaryHeader, conversationManager]);

  // Copy summary to clipboard
  const copySummaryToClipboard = useCallback(async () => {
    if (!conversationSummary) {
      throw new Error('No summary available to copy');
    }
    
    const headerText = summaryHeader ? `# ${summaryHeader}\n\n` : '';
    const summaryText = `${headerText}${conversationSummary}\n\n${emotionalInsights || ''}`;
    const success = await ExportService.copyToClipboard(summaryText);
    
    if (!success) {
      throw new Error('Failed to copy');
    }
  }, [conversationSummary, summaryHeader, emotionalInsights]);

  // Save journal entry
  const saveJournalEntry = useCallback(async () => {
    if (!conversationSummary && !emotionalInsights) {
      throw new Error('No summary available to save');
    }
        
    try {
      const journalData = {
        header: summaryHeader || 'Today\'s Reflection',
        summary: conversationSummary,
        insights: emotionalInsights,
        conversation: conversationManager?.conversation || [],
        sessionStartTime: conversationManager?.sessionStartTime,
        userId: conversationManager?.userId || 'user_' + Date.now(),
      };
            
      console.log('🔥 [Journal Hook] Saving journal data:', journalData);
            
      const savedJournal = journalStorageService.saveJournal(journalData);
      
      console.log('🔥 [Journal Hook] Journal saved successfully:', savedJournal);
      setShowViewJournalButton(true);
            
      return savedJournal;
          
    } catch (error) {
      console.error('Journal save error:', error);
      throw error;
    }
  }, [conversationSummary, emotionalInsights, summaryHeader, conversationManager, journalStorageService]);

  // =============================================================================
  // RETURN HOOK API
  // =============================================================================

  return {
    // States
    conversationSummary,
    emotionalInsights,
    summaryHeader,
    isGeneratingSummary,
    isAutoSaved,
    showSummary,
    showViewJournalButton,
    displayedHeader,
    displayedSummary,
    displayedInsights,
    journalRequested,
    takingNoteMessage,
    journalButtonClicked,
    processingJournalRequest,
    
    // Actions
    generateConversationSummary,
    handleJournalNavClick,
    handleViewJournalClick,
    downloadJournal,
    exportConversationSummary,
    copySummaryToClipboard,
    saveJournalEntry,
    autoSaveExistingJournal,
    clearJournal,
    typeHeaderText,
    
    // Setters
    setShowSummary,
    setJournalRequested,
    setTakingNoteMessage,
    setJournalButtonClicked,
    setShowViewJournalButton,
    setConversationSummary,
    setEmotionalInsights,
    setSummaryHeader,
    setDisplayedHeader,
    setDisplayedSummary,
    setDisplayedInsights,
    generateSummaryForEmptyConversation, 
      isLoadingJournal,
  setIsLoadingJournal,

  };
};